import supportApi from "support/api/supportApi";

// The poll rung, and for the pilot the whole product. MySQL is the source of
// truth: /sync is keyset on (conversation_id, id), so the watermark is the only
// state worth keeping and a missed tick costs nothing but latency.
//
// The interval is whatever widget-config asked for and is NOT stretched while
// the tab is hidden. Chrome already clamps background timers to about once a
// minute and freezes them entirely after a few minutes hidden, so stacking our
// own multiplier on top does not save requests, it just pushes the timer deep
// enough into throttling territory that a backgrounded thread stops updating
// at all. Cheapness in the background is the browser's job; ours is to catch up
// the moment the tab is looked at again, which is what the visibility handler
// below does.
const MAX_CONSECUTIVE_FAILURES = 3;

export default class PollTransport {
  constructor({ uuid, sinceId, intervalMs, onMessages, onState }) {
    this.uuid = uuid;
    this.watermark = sinceId || 0;
    this.intervalMs = intervalMs || 12000;
    this.onMessages = onMessages || (() => {});
    this.onState = onState || (() => {});
    this.timer = null;
    this.failures = 0;
    this.running = false;
    this.inFlight = false;
    // Coming back to the tab is the moment to catch up: whatever the browser
    // did to our timer while it was hidden, the thread is current again here.
    this.onVisibility = () => {
      if (!document.hidden) {
        this.tickNow();
      }
    };
  }

  start() {
    if (this.running) {
      return;
    }
    this.running = true;
    document.addEventListener("visibilitychange", this.onVisibility);
    this.tickNow();
  }

  currentInterval() {
    // Back off on a failing endpoint instead of hammering it at full rate.
    return this.failures
      ? Math.min(this.intervalMs * Math.pow(2, this.failures), 120000)
      : this.intervalMs;
  }

  schedule() {
    if (!this.running) {
      return;
    }
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => this.tick(), this.currentInterval());
  }

  // Never clear the armed timer without starting a request in its place. An
  // in-flight request reschedules when it finishes, so nudging during one is a
  // no-op rather than a way to strand the loop with no timer at all.
  tickNow() {
    if (this.inFlight) {
      return;
    }
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.tick();
  }

  async tick() {
    if (!this.running || this.inFlight) {
      return;
    }
    this.inFlight = true;
    const response = await supportApi.sync(this.uuid, {
      since_id: this.watermark,
      limit: 100,
    });
    this.inFlight = false;

    if (!this.running) {
      return;
    }

    if (response.status !== 200 || !response.data) {
      this.failures += 1;
      if (this.failures >= MAX_CONSECUTIVE_FAILURES) {
        this.onState("degraded");
      }
      this.schedule();
      return;
    }

    this.failures = 0;
    this.onState("polling");

    const messages = Array.isArray(response.data.messages) ? response.data.messages : [];
    if (messages.length) {
      this.onMessages(messages);
    }
    if (typeof response.data.watermark === "number") {
      this.watermark = response.data.watermark;
    }
    this.schedule();
  }

  stop() {
    this.running = false;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    document.removeEventListener("visibilitychange", this.onVisibility);
  }
}
